import React, { useState, useEffect } from "react";
import FacebookLogin from "react-facebook-login";
import {
  exchangeForLongLivedToken,
  refreshLongLivedToken,
  validateToken,
  getComprehensiveLeadsData,
  getAdAccounts,
  getPages,
  getCampaigns,
  getLeads,
} from "../../../api/facebookBackendApi";
import { teamAPI } from "../../../api/apiHelper";
import useToast from "../../../hooks/useToast";
import Toast from "../../../components/Toast";
import "./MetaLeadsPage.css";

// No mock data - only real Facebook API calls

// Facebook App ID - should be set in environment variables
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "your-app-id";

// Check if token is expired
const isTokenExpired = (timestamp, expiresIn) => {
  const now = Date.now();
  const tokenAge = now - timestamp;
  const tokenLifetime = expiresIn * 1000; // Convert to milliseconds
  return tokenAge >= tokenLifetime;
};

const MetaLeadsPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [sdkAvailable, setSdkAvailable] = useState(true);
  const { role } = JSON.parse(localStorage.getItem("userData"));

  // Toast notifications
  const {
    toast,
    success: showSuccess,
    error: showError,
    hideToast,
  } = useToast();

  // Lead details modal state
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);

  // Team member assignment state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [leadToAssign, setLeadToAssign] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [assigningLead, setAssigningLead] = useState(false);

  // Additional data states
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState("");
  const [comprehensiveData, setComprehensiveData] = useState(null);

  // Load team members on component mount
  const loadTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      const response = await teamAPI.getTeamMembers();
      console.log("response: ", response);
      if (response && response.data.invites) {
        // Filter only completed invites (registered team members) with valid _id
        const registeredMembers = response.data.invites.filter(
          (invite) => invite.status === "complete" && invite.role && invite._id
        );
        setTeamMembers(registeredMembers);
      }
    } catch (error) {
      console.error("Failed to load team members:", error);
      showError(
        "Something went wrong while loading team members. Please try again."
      );
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  // Check if Facebook App ID is configured and restore session
  useEffect(() => {
    if (FACEBOOK_APP_ID === "your-app-id") {
      setSdkAvailable(false);
      setError(
        "Facebook App ID not configured. You can still view demo leads below."
      );
      return;
    }

    // Check for existing token in localStorage
    const savedToken = localStorage.getItem("facebookToken");
    if (savedToken) {
      try {
        const tokenData = JSON.parse(savedToken);

        // Check if token is expired
        if (isTokenExpired(tokenData.timestamp, tokenData.expiresIn)) {
          // Token is expired, try to refresh it
          refreshLongLivedToken(tokenData.accessToken)
            .then((newToken) => {
              const updatedTokenData = {
                ...tokenData,
                accessToken: newToken.access_token,
                expiresIn: newToken.expires_in,
                timestamp: Date.now(),
              };

              localStorage.setItem(
                "facebookToken",
                JSON.stringify(updatedTokenData)
              );
              setIsLoggedIn(true);
              setUserInfo(updatedTokenData);
              loadLeads();
            })
            .catch((error) => {
              console.error("Failed to refresh token:", error);
              localStorage.removeItem("facebookToken");
            });
        } else {
          // Token is still valid
          setIsLoggedIn(true);
          setUserInfo(tokenData);
          // Auto-load leads when user is logged in
          loadLeads();
        }
      } catch (error) {
        console.error("Error parsing saved token:", error);
        localStorage.removeItem("facebookToken");
      }
    }

    // Load team members on component mount
    loadTeamMembers();
  }, []);

  // Facebook login success callback
  const responseFacebook = async (response) => {
    console.log("Facebook login response:", response);

    if (response.accessToken) {
      try {
        // Get current user ID from localStorage
        const userId = localStorage.getItem("userId");

        // Exchange short-lived token for long-lived token
        const tokenResponse = await exchangeForLongLivedToken(
          response.accessToken,
          userId
        );

        if (tokenResponse.success) {
          const longLivedToken = tokenResponse.data.access_token;

          setIsLoggedIn(true);
          setUserInfo({
            accessToken: longLivedToken,
            userID: response.userID,
            name: response.name,
            email: response.email,
            expiresIn: tokenResponse.data.expires_in,
            tokenType: tokenResponse.data.token_type,
          });

          // Store token info in localStorage for persistence
          localStorage.setItem(
            "facebookToken",
            JSON.stringify({
              accessToken: longLivedToken,
              userID: response.userID,
              name: response.name,
              email: response.email,
              expiresIn: tokenResponse.data.expires_in,
              tokenType: tokenResponse.data.token_type,
              timestamp: Date.now(),
            })
          );

          setError(null);
          setSuccess("Successfully connected to Facebook!");
          loadLeads();
          // Auto-load related data
          // await loadInitialData(longLivedToken);
        } else {
          setError(tokenResponse.message || "Failed to exchange token");
        }
      } catch (error) {
        console.error("Error exchanging token:", error);
        setError("Failed to get long-lived token. Please try again.");
      }
    } else {
      setError("Facebook login failed. Please try again.");
    }
  };

  // Facebook login failure callback
  const responseFacebookError = (error) => {
    console.error("Facebook login error:", error);
    setError("Facebook login was cancelled or failed. Please try again.");
  };

  const handleLogout = () => {
    // Clear stored token
    localStorage.removeItem("facebookToken");

    setIsLoggedIn(false);
    setUserInfo(null);
    setLeads([]);
    setAdAccounts([]);
    setPages([]);
    setCampaigns([]);
    setComprehensiveData(null);
    setError(null);
    setSuccess(null);
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    setError(null);
    setSuccess(null);

    try {
      // Get access token from localStorage
      const savedToken = localStorage.getItem("facebookToken");
      if (!savedToken) {
        setError("No access token available. Please log in again.");
        return;
      }

      const tokenData = JSON.parse(savedToken);
      const accessToken = tokenData.accessToken;

      if (!accessToken) {
        setError("No access token available. Please log in again.");
        return;
      }

      // First validate the token
      const tokenValidation = await validateToken(accessToken);

      if (!tokenValidation.valid) {
        setError("Access token is invalid. Please log in again.");
        handleLogout();
        return;
      }

      // Get comprehensive leads data (includes ad accounts, leads, campaigns)
      const comprehensiveData = await getComprehensiveLeadsData(accessToken, {
        limit: 50,
      });

      // Store comprehensive data for display
      setComprehensiveData(comprehensiveData);

      if (!comprehensiveData.leads || comprehensiveData.leads.length === 0) {
        if (comprehensiveData.message) {
          setError(comprehensiveData.message);
        } else {
          setError(
            "No leads found. Please make sure you have lead generation forms in your Facebook ad campaigns."
          );
        }
        setLeads([]);
        return;
      }

      // Transform the data to match our UI format
      const transformedLeads = comprehensiveData.leads.map((lead, index) => {
        // Extract dynamic fields from field_data
        const fieldData = lead.field_data || [];
        const extractFieldValue = (fieldName) => {
          const field = fieldData.find(
            (f) => f.name.toLowerCase() === fieldName.toLowerCase()
          );
          return field?.values?.[0] || null;
        };

        // Get common fields with fallbacks
        const firstName =
          extractFieldValue("first name") || extractFieldValue("first_name");
        const lastName =
          extractFieldValue("last name") || extractFieldValue("last_name");
        const fullName =
          firstName && lastName
            ? `${firstName} ${lastName}`
            : firstName || lead.name || "Unknown";

        return {
          id: lead.id || `lead-${index}`,
          name: fullName,
          email: extractFieldValue("email") || lead.email || "No email",
          phone:
            extractFieldValue("phone") ||
            extractFieldValue("phone_number") ||
            lead.phone ||
            "No phone",
          created_time: lead.created_time,
          ad_name: lead.campaign_name || `Ad ${lead.ad_id || "Unknown"}`,
          status: "new", // You can implement status logic based on your business rules
          company:
            extractFieldValue("company") ||
            extractFieldValue("company_name") ||
            lead.company ||
            "No company",
          job_title:
            extractFieldValue("job_title") ||
            extractFieldValue("job_position") ||
            lead.job_title ||
            "No title",
          ad_id: lead.ad_id,
          campaign_id: lead.campaign_id,
          campaign_status: lead.campaign_status,
          form_name: lead.form_name || "Unknown Form",
          form_id: lead.form_id,
          city: extractFieldValue("city") || lead.city || "No city",
          state: extractFieldValue("state") || lead.state || "No state",
          country: extractFieldValue("country") || lead.country || "No country",
          website: extractFieldValue("website") || lead.website || "No website",
          message: extractFieldValue("message") || lead.message || "No message",
          // Store all field_data for dynamic display
          field_data: fieldData,
          // Store original lead data
          original_lead: lead,
        };
      });

      setLeads(transformedLeads);
      setSuccess(`Successfully loaded ${transformedLeads.length} leads!`);

      // Update ad accounts and pages if not already loaded
      if (
        comprehensiveData.adAccounts &&
        comprehensiveData.adAccounts.length > 0
      ) {
        setAdAccounts(comprehensiveData.adAccounts);
      }
      if (comprehensiveData.pages && comprehensiveData.pages.length > 0) {
        setPages(comprehensiveData.pages);
      }
      if (
        comprehensiveData.campaigns &&
        comprehensiveData.campaigns.length > 0
      ) {
        setCampaigns(comprehensiveData.campaigns);
      }

      // Log additional info for debugging
      console.log("Comprehensive data received:", {
        totalLeads: comprehensiveData.totalLeads,
        adAccountId: comprehensiveData.adAccountId,
        adAccountName: comprehensiveData.adAccountName,
        campaignsCount: comprehensiveData.campaigns?.length || 0,
        adAccountsCount: comprehensiveData.adAccounts?.length || 0,
        pagesCount: comprehensiveData.pages?.length || 0,
        leadgenFormsCount: comprehensiveData.leadgenForms?.length || 0,
      });
    } catch (err) {
      console.error("Error loading leads:", err);
      setError(`Failed to load leads: ${err.message}`);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      new: "#1976d2",
      contacted: "#ff9800",
      qualified: "#4caf50",
      converted: "#8bc34a",
    };
    return statusColors[status] || "#9e9e9e";
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      new: "New",
      contacted: "Contacted",
      qualified: "Qualified",
      converted: "Converted",
    };
    return statusLabels[status] || status;
  };

  const getTokenExpiryInfo = () => {
    if (!userInfo?.timestamp || !userInfo?.expiresIn) return null;

    const now = Date.now();
    const tokenAge = now - userInfo.timestamp;
    const tokenLifetime = userInfo.expiresIn * 1000;
    const remainingTime = tokenLifetime - tokenAge;
    const remainingDays = Math.floor(remainingTime / (1000 * 60 * 60 * 24));

    return {
      remainingDays,
      isExpiringSoon: remainingDays <= 7,
    };
  };

  // Handle lead click to show details
  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  // Handle assign button click
  const handleAssignLead = (lead, event) => {
    event.stopPropagation(); // Prevent lead click
    setLeadToAssign(lead);
    setSelectedTeamMember(null);
    setShowAssignModal(true);
  };

  // Handle team member selection and assignment
  const handleTeamMemberAssignment = async () => {
    if (!selectedTeamMember || !leadToAssign) {
      showError("Please select a team member to assign the lead to.");
      return;
    }

    setAssigningLead(true);
    try {
      await teamAPI.assignLead(leadToAssign.id, selectedTeamMember._id);
      showSuccess(`Lead "${leadToAssign.name}" assigned successfully!`);
      setShowAssignModal(false);
      setLeadToAssign(null);
      setSelectedTeamMember(null);

      // Optionally refresh leads to show updated assignment
      // loadLeads();
    } catch (error) {
      console.error("Failed to assign lead:", error);
      showError(
        "Something went wrong while assigning the lead. Please try again."
      );
    } finally {
      setAssigningLead(false);
    }
  };

  // Close assignment modal
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setLeadToAssign(null);
    setSelectedTeamMember(null);
  };

  // Close lead modal
  const closeLeadModal = () => {
    setShowLeadModal(false);
    setSelectedLead(null);
  };

  // handle code for lead
  return (
    <div className="meta-leads-page">
      <div className="meta-leads-header">
        <h1>Meta Leads</h1>
        <p>Connect your Facebook account to view and manage your ad leads</p>
      </div>

      {!isLoggedIn ? (
        <div className="login-section">
          <div className="login-card">
            <div className="login-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  fill="#1877F2"
                />
              </svg>
            </div>
            <h2>Connect Facebook Account</h2>
            <p>
              Connect your Facebook account to access your ad leads and manage
              them effectively. All data will be automatically saved to your
              database.
            </p>
            <p className="permissions-text">
              <strong>Required permissions:</strong>
              <br />• <code>ads_management</code> - Access to ad management data
              <br />• <code>leads_retrieval</code> - Access to lead data from
              forms
              <br />• <code>pages_show_list</code> - Access to your Facebook
              pages
              <br />• <code>pages_read_engagement</code> - Read page engagement
              data
              <br />• <code>pages_manage_ads</code> - Manage page ads
              <br />• <code>pages_manage_metadata</code> - Manage page metadata
              (for webhooks)
              <br />
              <br />
              <strong>Note:</strong> You need access to Facebook pages because
              lead generation forms are associated with pages, not ad accounts.
            </p>

            {!sdkAvailable && (
              <div className="error-message">
                <p>
                  <strong>Facebook SDK Not Available:</strong> Please check your
                  Facebook App ID configuration.
                </p>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {sdkAvailable ? (
              <FacebookLogin
                appId={FACEBOOK_APP_ID}
                autoLoad={false}
                fields="name,email,picture"
                scope="ads_management,leads_retrieval,pages_show_list,pages_read_engagement,pages_manage_ads,pages_manage_metadata"
                callback={responseFacebook}
                onFailure={responseFacebookError}
                render={({ onClick, isDisabled, isProcessing }) => (
                  <button
                    className="facebook-login-btn"
                    onClick={onClick}
                    disabled={isDisabled || isProcessing || role !== "member"}
                  >
                    {isProcessing ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                            fill="white"
                          />
                        </svg>
                        Connect with Facebook
                      </>
                    )}
                  </button>
                )}
              />
            ) : (
              <button className="facebook-login-btn" disabled={true}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                    fill="white"
                  />
                </svg>
                Facebook SDK Not Available
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="leads-dashboard">
          <div className="dashboard-header">
            <div className="user-info">
              <h2>Welcome back{userInfo?.name ? `, ${userInfo.name}` : ""}!</h2>
              {/* <p>User ID: {userInfo?.userID}</p> */}
              {/* <p>Access Token: {userInfo?.accessToken?.substring(0, 20)}...</p> */}
              {(() => {
                const tokenInfo = getTokenExpiryInfo();
                if (tokenInfo) {
                  return (
                    <p
                      className={`token-expiry ${
                        tokenInfo.isExpiringSoon ? "expiring-soon" : ""
                      }`}
                    >
                      Token expires in {tokenInfo.remainingDays} days
                      {tokenInfo.isExpiringSoon && " (Will auto-refresh)"}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            <div className="dashboard-actions">
              <button
                className="refresh-btn"
                onClick={loadLeads}
                disabled={loadingLeads}
              >
                {loadingLeads ? "Refreshing..." : "Refresh Leads"}
              </button>
              <button className="logout-btn" onClick={handleLogout}>
                Disconnect
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="leads-section">
            <div className="leads-header">
              <h3>Leads Dashboard</h3>
              <span className="leads-count">{leads.length} leads found</span>
            </div>

            {loadingLeads ? (
              <div className="loading-container">
                <div className="loading-spinner large"></div>
                <p>Loading leads...</p>
              </div>
            ) : leads.length === 0 ? (
              <div className="no-leads">
                <p>
                  No leads found in your Facebook ad campaigns. Make sure you
                  have:
                </p>
                <ul style={{ textAlign: "left", marginTop: "10px" }}>
                  <li>
                    Access to Facebook <strong>Pages</strong> (leads are
                    associated with pages)
                  </li>
                  <li>
                    Active Facebook ad campaigns with{" "}
                    <strong>Lead Generation</strong> objective
                  </li>
                  <li>
                    Lead generation forms created and published on your pages
                  </li>
                  <li>
                    Proper permissions for <code>leads_retrieval</code> and{" "}
                    <code>pages_show_list</code>
                  </li>
                  <li>
                    Leads generated from your forms (not just campaigns running)
                  </li>
                </ul>
                <p
                  style={{
                    marginTop: "15px",
                    fontSize: "0.9rem",
                    color: "#888",
                  }}
                >
                  <strong>Note:</strong> Leads are only available if you have
                  created lead generation forms on your Facebook pages and have
                  received actual form submissions.
                </p>
              </div>
            ) : (
              <div className="leads-table-container">
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Company</th>
                      <th>Job Title</th>
                      <th>Campaign</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="lead-row"
                        onClick={() => handleLeadClick(lead)}
                      >
                        <td className="lead-name">{lead.name}</td>
                        <td className="lead-email">{lead.email}</td>
                        <td className="lead-phone">{lead.phone}</td>
                        <td className="lead-company">{lead.company}</td>
                        <td className="lead-title">{lead.job_title}</td>
                        <td className="lead-campaign">{lead.ad_name}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: getStatusColor(lead.status),
                            }}
                          >
                            {getStatusLabel(lead.status)}
                          </span>
                        </td>
                        <td className="lead-date">
                          {formatDate(lead.created_time)}
                        </td>
                        <td className="lead-actions">
                          <button
                            className="assign-btn"
                            onClick={(e) => handleAssignLead(lead, e)}
                            title="Assign Lead"
                          >
                            Assign
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lead Details Modal */}
      {showLeadModal && selectedLead && (
        <div className="modal-overlay" onClick={closeLeadModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Lead Details</h3>
              <button className="close-btn" onClick={closeLeadModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="lead-details-grid">
                <div className="detail-section">
                  <h4>Contact Information</h4>
                  <div className="detail-item">
                    <strong>Name:</strong> {selectedLead.name}
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> {selectedLead.email}
                  </div>
                  <div className="detail-item">
                    <strong>Phone:</strong> {selectedLead.phone}
                  </div>
                  <div className="detail-item">
                    <strong>Company:</strong> {selectedLead.company}
                  </div>
                  <div className="detail-item">
                    <strong>Job Title:</strong> {selectedLead.job_title}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Location</h4>
                  <div className="detail-item">
                    <strong>City:</strong> {selectedLead.city}
                  </div>
                  <div className="detail-item">
                    <strong>State:</strong> {selectedLead.state}
                  </div>
                  <div className="detail-item">
                    <strong>Country:</strong> {selectedLead.country}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Campaign Information</h4>
                  <div className="detail-item">
                    <strong>Campaign:</strong> {selectedLead.ad_name}
                  </div>
                  <div className="detail-item">
                    <strong>Form Name:</strong> {selectedLead.form_name}
                  </div>
                  <div className="detail-item">
                    <strong>Ad ID:</strong> {selectedLead.ad_id}
                  </div>
                  <div className="detail-item">
                    <strong>Campaign ID:</strong> {selectedLead.campaign_id}
                  </div>
                  <div className="detail-item">
                    <strong>Form ID:</strong> {selectedLead.form_id}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Additional Information</h4>
                  <div className="detail-item">
                    <strong>Status:</strong>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(selectedLead.status),
                        marginLeft: "8px",
                      }}
                    >
                      {getStatusLabel(selectedLead.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Created:</strong>{" "}
                    {formatDate(selectedLead.created_time)}
                  </div>
                  <div className="detail-item">
                    <strong>Website:</strong>{" "}
                    {selectedLead.website || "Not provided"}
                  </div>
                  {selectedLead.message &&
                    selectedLead.message !== "No message" && (
                      <div className="detail-item">
                        <strong>Message:</strong> {selectedLead.message}
                      </div>
                    )}
                </div>

                {/* Dynamic Fields Section */}
                {selectedLead.field_data &&
                  selectedLead.field_data.length > 0 && (
                    <div className="detail-section">
                      <h4>Form Fields</h4>
                      {selectedLead.field_data.map((field, index) => (
                        <div key={index} className="detail-item">
                          <strong>{field.name}:</strong>{" "}
                          {field.values ? field.values.join(", ") : "No value"}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="assign-btn modal-assign"
                onClick={() =>
                  handleAssignLead(selectedLead, {
                    stopPropagation: () => {},
                  })
                }
              >
                Assign Lead
              </button>
              <button className="close-btn-secondary" onClick={closeLeadModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Member Assignment Modal */}
      {showAssignModal && leadToAssign && (
        <div className="modal-overlay" onClick={closeAssignModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Lead to Team Member</h3>
              <button className="close-btn" onClick={closeAssignModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="assignment-info">
                <h4>Lead Information</h4>
                <div className="lead-summary">
                  <p>
                    <strong>Name:</strong> {leadToAssign.name}
                  </p>
                  <p>
                    <strong>Email:</strong> {leadToAssign.email}
                  </p>
                  <p>
                    <strong>Company:</strong> {leadToAssign.company}
                  </p>
                </div>
              </div>

              <div className="team-member-selection">
                <h4>Select Team Member</h4>
                {loadingTeamMembers ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading team members...</p>
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="no-team-members">
                    <p>
                      No team members available. Please invite team members
                      first.
                    </p>
                  </div>
                ) : (
                  <div className="team-member-list">
                    {teamMembers.map((member) => (
                      <div
                        key={member.email}
                        className={`team-member-option ${
                          selectedTeamMember &&
                          selectedTeamMember.email === member.email
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => setSelectedTeamMember(member)}
                      >
                        <div className="member-info">
                          <div className="member-name">
                            {member.name || "Unknown"}
                          </div>
                          <div className="member-email">{member.email}</div>
                          <div className="member-role">{member.role}</div>
                        </div>
                        <div className="selection-indicator">
                          {selectedTeamMember &&
                            selectedTeamMember.email === member.email &&
                            "✓"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="assign-btn modal-assign"
                onClick={handleTeamMemberAssignment}
                disabled={!selectedTeamMember || assigningLead}
              >
                {assigningLead ? "Assigning..." : "Assign Lead"}
              </button>
              <button
                className="close-btn-secondary"
                onClick={closeAssignModal}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        title={toast.title}
        duration={toast.duration}
        position={toast.position}
        onClose={hideToast}
      />
    </div>
  );
};

export default MetaLeadsPage;
